import {
  Component, EventEmitter, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {
  FormControl, FormGroup, Validators,
  AbstractControl
} from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  Observable, of, Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { SegmentService } from '@sondermind/segment';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { ConfigurationService } from '@sondermind/configuration';
import { ReferralSourceEnum } from '@sondermind/data-access/client-referral';
import { ISocialIntakeForm, SocialIntakeFormValues } from '../../../models/social-intake-form.model';
import { PaymentLocationService } from '../../../services/payment-location.service';
import {
  IQuickLeadFormChangedData,
  IQuickLeadIntakeSegmentSchema,
  QuickLeadIntakeSegmentEvents
} from '../../../models/segment/quick-lead-intake-segment-schema.interface';
import { FormVersion } from '../../../models/enums/form-version.enum';
import { QuickLeadIntakeStore } from '../../../stores/quick-lead-intake.store';
import { SocialIntakeFormState } from '../../../models/enums/social-intake-form-state.enum';

type SocialIntakeFormKeys = keyof ISocialIntakeForm;

@Component({
  selector: 'social-intake-form',
  templateUrl: './social-intake-form.component.html',
  styleUrls: ['./social-intake-form.component.scss'],
  providers: [PaymentLocationService]
})
export class SocialIntakeFormComponent implements OnInit, OnDestroy {
  socialIntakeForm!: FormGroup<ISocialIntakeForm>;
  availableInYourState: boolean = true;
  acceptedInsurances$: Observable<Array<DropdownOption<number>>> = of([]);
  acceptedInsurances: Array<DropdownOption<number>> = [];
  paymentTypesInState$: Observable<Array<DropdownOption<string>>> = of([]);
  showInsurancePlans$: Observable<boolean> = of(false);
  providerDirectoryUrl!: string;

  @Output() formStateChange = new EventEmitter<SocialIntakeFormState>();
  private formStateSubject = new BehaviorSubject<SocialIntakeFormState>(SocialIntakeFormState.FORM);
  get currentFormState(): SocialIntakeFormState {
    return this.formStateSubject.value;
  }

  socialIntakeFormState = SocialIntakeFormState;
  private destroyed$ = new Subject<void>();

  constructor(
    private paymentLocationService: PaymentLocationService,
    private configurationService: ConfigurationService,
    private segmentService: SegmentService<IQuickLeadIntakeSegmentSchema>,
    private auth0: AuthService,
    private quickLeadIntakeStore: QuickLeadIntakeStore,
  ) { }

  ngOnInit(): void {
    this.setupForm();
    this.fireFormStartedSegmentEvent();
    this.subscribeToFormStateChange();
    this.getValuesFromPaymentLocationService();
    this.subscribeToMedicationCheckbox();
    this.setupFormChangedSubscriptions();

    this.providerDirectoryUrl = this.configurationService.env.providerDirectoryBase;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setupForm(): void {
    this.socialIntakeForm = new FormGroup<ISocialIntakeForm>({
      openPreferences: new FormControl<string | null>(null, [Validators.required]),
      medicationManagement: new FormControl<boolean>(false, { nonNullable: true }),
      firstName: new FormControl<string | null>(null, [Validators.required]),
      lastName: new FormControl<string | null>(null, [Validators.required]),
      location: this.paymentLocationService.locationControl,
      paymentType: this.paymentLocationService.paymentTypeControl,
      insurance: this.paymentLocationService.insuranceControl,
    });
  }

  subscribeToFormStateChange(): void {
    this.formStateSubject.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((state) => {
      this.formStateChange.emit(state);
    });
  }

  getValuesFromPaymentLocationService(): void {
    this.paymentLocationService.getAvailableInYourState$().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((val) => {
      this.availableInYourState = val;
    });

    this.paymentTypesInState$ = this.paymentLocationService.getPaymentTypesInState$();
    this.acceptedInsurances$ = this.paymentLocationService.getAcceptedInsurances$();
    this.acceptedInsurances$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((x) => {
        this.acceptedInsurances = x;
      });
    this.showInsurancePlans$ = this.paymentLocationService.getShowInsurancePlans$();
  }

  locationSelected(selectedAddress: google.maps.places.PlaceResult): void {
    this.paymentLocationService.locationSelected(selectedAddress);
  }

  subscribeToMedicationCheckbox(): void {
    this.socialIntakeForm.controls.medicationManagement.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      this.paymentLocationService.setMedicationManagement(value);
    });
  }

  continue(): void {
    this.formStateSubject.next(SocialIntakeFormState.PROCESSING);
    this.fireFormSubmittedSegmentEvent();
    const formValue = this.socialIntakeForm.value as SocialIntakeFormValues;

    this.quickLeadIntakeStore.submitReferralRequest$(formValue).pipe(
      catchError(() => {
        this.formStateSubject.next(SocialIntakeFormState.ERROR);
        return EMPTY;
      }),
      switchMap((response) => {
        if (!response) {
          this.formStateSubject.next(SocialIntakeFormState.ERROR);
          return EMPTY;
        }
        return this.auth0.loginWithRedirect({
          authorizationParams: {
            scope: 'offline_access',
            screen_hint: 'signup',
            signup_type: 'Direct',
            saga_source: ReferralSourceEnum.DIRECT_REFERRAL,
            saga_id: response.saga_id,
            first_name: formValue.firstName,
            last_name: formValue.lastName,
            eeNamespace: this.configurationService.env.eeNamespace
          }
        });
      })
    ).subscribe();
  }

  setupFormChangedSubscriptions(): void {
    Object.keys(this.socialIntakeForm.controls).forEach((key) => {
      this.setupControlSubscription(key, this.socialIntakeForm.get(key));
    });
  }

  setupControlSubscription(key: string, control: AbstractControl | null): void {
    if (!control) return;

    const isNestedGroup = control instanceof FormGroup && key !== 'location';
    const controls: Array<[string, AbstractControl]> = isNestedGroup
      ? Object.entries(control.controls)
      : [[key, control]];

    controls.forEach(([controlKey, ctrl]: [string, AbstractControl]) => {
      ctrl.valueChanges.pipe(
        filter((x) => !(x === null || x === undefined)),
        take(1),
        takeUntil(this.destroyed$)
      ).subscribe({
        next: (val) => {
          const eventData: IQuickLeadFormChangedData = {
            fieldName: controlKey,
            formVersion: FormVersion.SOCIAL_SIGN_UP
          };

          const transformedValue = this.transformFieldValueForSegment(key, val);
          if (transformedValue !== undefined) {
            eventData.value = transformedValue;
          }

          this.sendSegmentEvent(
            QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_CHANGED, eventData
          );
        },
      });
    });
  }

  backToForm(): void {
    this.formStateSubject.next(SocialIntakeFormState.FORM);
  }

  transformFieldValueForSegment(fieldName: string, value: unknown): string | boolean | undefined {
    switch (fieldName as SocialIntakeFormKeys) {
      case 'insurance':
        return this.acceptedInsurances.find((x) => x.value === value)?.name;
      case 'paymentType':
      case 'medicationManagement':
        return value as string | boolean;
      default:
        return undefined;
    }
  }

  fireFormStartedSegmentEvent(): void {
    this.sendSegmentEvent(
      QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_STARTED,
      { formVersion: FormVersion.SOCIAL_SIGN_UP }
    );
  }

  fireFormSubmittedSegmentEvent(): void {
    this.sendSegmentEvent(
      QuickLeadIntakeSegmentEvents.QUICK_LEAD_FORM_SUBMITTED,
      { formVersion: FormVersion.SOCIAL_SIGN_UP }
    );
  }

  private sendSegmentEvent<T extends keyof IQuickLeadIntakeSegmentSchema>(
    segmentEventType: T, data: IQuickLeadIntakeSegmentSchema[T]
  ): void {
    this.segmentService.track<T>(segmentEventType, data).catch(() => { });
  }
}
